import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, message, Alert } from 'antd';
import { LockOutlined, KeyOutlined, UnlockOutlined } from '@ant-design/icons';
import { settingsService } from '../services/api';

const { Title, Text } = Typography;

const PIN_LENGTH = 4;
const PIN_REGEX = /^\d{4}$/;

const numericKeyDownFilter = (e) => {
  const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
  if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
  if (!/^\d$/.test(e.key)) e.preventDefault();
};

const AdminPin = () => {
  const [pinForm] = Form.useForm();
  const [isPinSubmitting, setIsPinSubmitting] = useState(false);

  const handlePinSubmit = async (values) => {
    const { currentPin, newPin, confirmPin } = values;
    if (newPin !== confirmPin) {
      message.error('La confirmation ne correspond pas au nouveau PIN');
      return;
    }
    setIsPinSubmitting(true);
    try {
      await settingsService.updateAdminPin({ currentPin, newPin });
      message.success('PIN d\'accès modifié avec succès');
      pinForm.resetFields();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        'Erreur lors de la modification du PIN';
      message.error(apiMessage);
    } finally {
      setIsPinSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto 0 0' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Code PIN d'accès
        </Title>
        <Text type="secondary">
          Gérez le code PIN demandé avant d'ouvrir la page de connexion.
        </Text>
      </div>

      <Card
        title={
          <span>
            <UnlockOutlined style={{ marginRight: 8 }} />
            Modifier le code PIN ({PIN_LENGTH} chiffres)
          </span>
        }
        style={{ borderRadius: 12 }}
      >


        <Form
          form={pinForm}
          layout="vertical"
          onFinish={handlePinSubmit}
          autoComplete="off"
          requiredMark={false}
        >
          <Form.Item
            label="PIN actuel"
            name="currentPin"
            rules={[{ required: true, message: 'Le PIN actuel est requis' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Entrez le PIN actuel"
              size="large"
              maxLength={PIN_LENGTH}
              inputMode="numeric"
              autoComplete="off"
              onKeyDown={numericKeyDownFilter}
            />
          </Form.Item>

          <Form.Item
            label={`Nouveau PIN (${PIN_LENGTH} chiffres)`}
            name="newPin"
            rules={[
              { required: true, message: 'Le nouveau PIN est requis' },
              { pattern: PIN_REGEX, message: `Doit contenir exactement ${PIN_LENGTH} chiffres` },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<KeyOutlined />}
              placeholder={'•'.repeat(PIN_LENGTH)}
              size="large"
              maxLength={PIN_LENGTH}
              inputMode="numeric"
              autoComplete="off"
              onKeyDown={numericKeyDownFilter}
            />
          </Form.Item>

          <Form.Item
            label="Confirmer le nouveau PIN"
            name="confirmPin"
            dependencies={['newPin']}
            hasFeedback
            rules={[
              { required: true, message: 'Veuillez confirmer le PIN' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPin') === value) return Promise.resolve();
                  return Promise.reject(new Error('Les PIN ne correspondent pas'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<KeyOutlined />}
              placeholder={'•'.repeat(PIN_LENGTH)}
              size="large"
              maxLength={PIN_LENGTH}
              inputMode="numeric"
              autoComplete="off"
              onKeyDown={numericKeyDownFilter}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isPinSubmitting}
              size="large"
              icon={<UnlockOutlined />}
              style={{
                background: 'linear-gradient(135deg, #1677ff 0%, #0056d2 100%)',
                border: 'none',
              }}
            >
              Enregistrer le nouveau PIN
            </Button>
            <Button
              type="default"
              size="large"
              style={{ marginLeft: 12 }}
              onClick={() => pinForm.resetFields()}
              disabled={isPinSubmitting}
            >
              Annuler
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AdminPin;
